import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wallet, ShieldCheck, ArrowsCounterClockwise, Warning, PlugsConnected } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

const card = "bg-[#12151d] border border-[#232733] rounded-xl";
const inputCls = "w-full h-10 rounded-lg bg-[#0A0D14] border border-[#232733] px-3 text-xs text-white placeholder:text-[#4a5061] outline-none focus:border-[#FF6B35]";
const fmtT = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export const PaymentsTab = () => {
  const [cfg, setCfg] = useState(null);
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [webhook, setWebhook] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/payments/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (keyId.startsWith("rzp_live_") && !window.confirm("You are saving LIVE keys — all tenant payments become REAL charges immediately. Continue?")) return;
    setBusy("save");
    try {
      const { data } = await api.put("/admin/payments/config", { key_id: keyId, key_secret: secret, webhook_secret: webhook });
      toast.success(`Keys verified & saved — gateway is now in ${data.mode.toUpperCase()} mode`);
      setKeyId(""); setSecret(""); setWebhook("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/admin/payments/test");
      toast.success(`Connection OK — ${data.mode.toUpperCase()} mode (${data.key_id})`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const revert = async () => {
    if (!window.confirm("Remove the admin-saved keys and revert to the built-in environment keys?")) return;
    setBusy("revert");
    try {
      await api.delete("/admin/payments/config");
      toast.success("Reverted to environment keys");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };

  if (!cfg) return <p className="text-sm text-[#8a90a0]">Loading…</p>;
  const live = cfg.mode === "live";

  return (
    <div className="max-w-3xl space-y-5" data-testid="admin-payments-tab">
      <div className={`${card} p-5`} data-testid="payments-status-card">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="h-10 w-10 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center"><Wallet size={20} weight="duotone" className="text-[#FF6B35]" /></span>
          <div>
            <h3 className="text-sm font-bold text-white">Razorpay Payment Gateway</h3>
            <p className="text-[11px] text-[#8a90a0]">Powers all tenant payments — plan purchases and AI top-ups.</p>
          </div>
          <span data-testid="payments-mode-badge"
            className={`ml-auto px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${live ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
            {live ? "● Live mode" : "● Test mode"}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-[10px] font-bold uppercase text-[#8a90a0]">Source</div>
            <div className="mt-0.5 font-bold text-white" data-testid="payments-source">
              {cfg.source === "admin" ? "Admin Console" : cfg.source === "env" ? "Environment keys" : "Not configured"}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase text-[#8a90a0]">Key ID</div>
            <div className="mt-0.5 font-mono text-[#c9cfdb] truncate" data-testid="payments-key-id">{cfg.key_id || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase text-[#8a90a0]">Key Secret</div>
            <div className="mt-0.5 font-mono text-[#c9cfdb]" data-testid="payments-secret-mask">{cfg.secret_mask || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase text-[#8a90a0]">Webhook secret</div>
            <div className="mt-0.5 font-bold text-[#c9cfdb]">{cfg.webhook_secret_set ? "Set" : "Not set"}</div>
          </div>
        </div>
        {cfg.updated_at && (
          <p className="mt-3 text-[10px] text-[#8a90a0]">Last updated by {cfg.updated_by} · {fmtT(cfg.updated_at)}</p>
        )}
        <div className="mt-4 flex gap-2 flex-wrap">
          <button onClick={test} disabled={busy === "test"} data-testid="payments-test-btn"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#232733] hover:bg-[#2c3140] text-white text-xs font-bold disabled:opacity-60">
            <PlugsConnected size={13} /> {busy === "test" ? "Testing…" : "Test connection"}
          </button>
          {cfg.source === "admin" && (
            <button onClick={revert} disabled={busy === "revert"} data-testid="payments-revert-btn"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#232733] text-[#8a90a0] hover:text-white text-xs font-bold disabled:opacity-60">
              <ArrowsCounterClockwise size={13} /> Revert to environment keys
            </button>
          )}
        </div>
      </div>

      <form onSubmit={save} className={`${card} p-5`} data-testid="payments-form-card">
        <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldCheck size={16} className="text-[#FF6B35]" /> Update gateway keys</h3>
        <p className="mt-1 text-[11px] text-[#8a90a0]">
          Get these from Razorpay Dashboard → Account &amp; Settings → API Keys. Keys are verified against Razorpay before saving; the secret is stored securely and never shown again — only the last 4 characters.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Key ID *</label>
            <input value={keyId} onChange={(e) => setKeyId(e.target.value)} required placeholder="rzp_live_… or rzp_test_…"
              data-testid="payments-keyid-input" className={`${inputCls} mt-1 font-mono`} />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Key Secret *</label>
            <input value={secret} onChange={(e) => setSecret(e.target.value)} required type="password" placeholder="Your Razorpay key secret"
              data-testid="payments-secret-input" className={`${inputCls} mt-1 font-mono`} autoComplete="new-password" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Webhook Secret (optional)</label>
            <input value={webhook} onChange={(e) => setWebhook(e.target.value)} type="password" placeholder="From Razorpay → Webhooks (if configured)"
              data-testid="payments-webhook-input" className={`${inputCls} mt-1 font-mono`} autoComplete="new-password" />
          </div>
        </div>
        <button type="submit" disabled={busy === "save"} data-testid="payments-save-btn"
          className="mt-4 h-10 px-6 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white text-xs font-bold disabled:opacity-60">
          {busy === "save" ? "Verifying with Razorpay…" : "Save & Verify"}
        </button>
      </form>

      <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 flex gap-3" data-testid="payments-switch-note">
        <Warning size={16} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
        <p className="text-[11px] text-amber-200/90 leading-relaxed">
          <b>Switching keys?</b> Payments already collected on the previous keys stay in that Razorpay account — refunds, settlements and verification for those transactions must be handled from the old account's dashboard. New payments from this moment use the new keys. Entering <b>rzp_live_</b> keys makes every tenant payment a real charge immediately.
        </p>
      </div>
    </div>
  );
};
